/**
 * networth.js — Net worth calculation utilities
 *
 * Pure functions for computing net worth totals and grouping accounts by type.
 * Used by NetWorthPage for display logic.
 */

/**
 * Calculate net worth totals from an array of accounts.
 * @param {Array} accounts - Account objects with current_balance and is_asset fields
 * @returns {{ totalAssets: number, totalLiabilities: number, netWorth: number }}
 */
export function calculateNetWorth(accounts) {
  let totalAssets = 0;
  let totalLiabilities = 0;

  for (const acct of accounts) {
    const balance = acct.current_balance || 0;
    if (acct.is_asset !== false) {
      totalAssets += balance;
    } else {
      totalLiabilities += Math.abs(balance);
    }
  }

  return {
    totalAssets,
    totalLiabilities,
    netWorth: totalAssets - totalLiabilities,
  };
}

/**
 * Group accounts by type with subtotals per group.
 * @param {Array} accounts - Account objects with type and current_balance fields
 * @returns {Array<{ type: string, label: string, accounts: Array, subtotal: number }>}
 */
export function groupAccountsByType(accounts) {
  const typeConfig = {
    checking: { label: 'Checking', order: 0 },
    savings: { label: 'Savings', order: 1 },
    credit: { label: 'Credit Cards', order: 2 },
    investment: { label: 'Investments', order: 3 },
    brokerage: { label: 'Investments', order: 3 },
    loan: { label: 'Loans', order: 4 },
    other: { label: 'Other', order: 5 },
  };

  const groups = {};

  for (const acct of accounts) {
    const typeVal = acct.type && typeof acct.type === 'object' ? (acct.type.name || 'other') : (acct.type || 'other');
    const rawType = typeVal.toLowerCase();
    // Normalize similar types
    let type = rawType;
    if (rawType === 'brokerage' || rawType === 'investment') type = 'investment';
    if (rawType.includes('credit')) type = 'credit';
    if (rawType.includes('loan') || rawType === 'mortgage') type = 'loan';
    if (!typeConfig[type]) type = 'other';

    if (!groups[type]) {
      const config = typeConfig[type] || typeConfig.other;
      groups[type] = { type, label: config.label, order: config.order, accounts: [], subtotal: 0 };
    }
    groups[type].accounts.push(acct);
    groups[type].subtotal += acct.current_balance || 0;
  }

  return Object.values(groups).sort((a, b) => a.order - b.order);
}
